import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import { LoadingSpinner } from "../../../../../common/Loading";
import { ConfirmationModal } from "../../../../../common/Modal";
import { Input } from "../../../../../common/Input";

const MedicalClearanceProcessorModal = ({
  isOpen,
  onClose,
  selectedHeader,
  canProcess,
  user,
  showGeneralMessage,
  getAssignmentDetails,
  saveBatchDetails,
  finalizeClearance,
  isLoading
}) => {
  const [details, setDetails] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Derived state
  const allItemsChecked = details.length > 0 && details.every(d => d.isCompleted);
  const isAlreadyCompleted = selectedHeader?.isCompleted || selectedHeader?.status === "Completed";

  useEffect(() => {
    if (isOpen && selectedHeader) loadDetails();
  }, [isOpen, selectedHeader]);

  const loadDetails = async () => {
    setLocalLoading(true);
    const data = await getAssignmentDetails(selectedHeader.id);
    if (data) {
      setDetails(data.map(d => ({
        ...d,
        isCompleted: d.isCompleted || d.IsCompleted || false,
        bp: d.bp || d.Bp || "",
        remarks: d.remarks || d.Remarks || ""
      })));
    }
    setLocalLoading(false);
  };

  const handleInputChange = (id, field, value) => {
    setDetails(prev => prev.map(d => d.id === id ? { ...d, [field]: value } : d));
  };

  const handleToggle = (id) => {
    if (!canProcess || isAlreadyCompleted) return;
    setDetails(prev => prev.map(d => d.id === id ? { ...d, isCompleted: !d.isCompleted } : d));
  };

  const handleSaveProgress = async () => {
    const success = await saveBatchDetails(selectedHeader.id, details);
    if (success && allItemsChecked) {
      setShowConfirm(true);
    }
    return success; // ADDED: Return success for chaining
  };

  // MODIFIED: Now saves progress before finalization
  const handleFinalize = async () => {
    // 1. First save the current state of the checklist (BP, Remarks, and Checkboxes)
    const saveSuccess = await saveBatchDetails(selectedHeader.id, details);

    if (saveSuccess) {
      // 2. Only if save is successful, trigger the finalization
      const finalizeSuccess = await finalizeClearance(selectedHeader.id);
      if (finalizeSuccess) {
        setShowConfirm(false);
        onClose();
      }
    } else {
      showGeneralMessage("Could not save details. Finalization aborted.", false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-4" style={{ marginTop: "0px" }}>
      <div className="bg-white w-full max-w-4xl rounded-lg shadow-2xl flex flex-col relative max-h-[90vh]">

        <div className="flex justify-between rounded-t-lg items-center p-4 border-b bg-gray-50">
          <h3 className="font-bold text-gray-600 flex items-center gap-2">
            <MedicalInformationIcon className="text-orange-500" />
            Clearance Checklist & Processing
          </h3>
          <button onClick={onClose} className="bg-transparent border-none cursor-pointer text-gray-400 hover:text-gray-600">
            <CloseIcon />
          </button>
        </div>

        <div className="px-6 py-3 bg-blue-50 border-b border-blue-100 flex flex-wrap justify-between items-center gap-2">
          <div>
            <p className="text-[10px] uppercase font-bold text-blue-400 m-0">Employee</p>
            <p className="text-sm font-bold text-blue-900 m-0">{selectedHeader.employeeFullName}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase font-bold text-blue-400 m-0">Reference</p>
            <p className="text-sm font-mono text-blue-900 m-0">{selectedHeader.transactionNumber}</p>
          </div>
        </div>

        <div className="p-6 space-y-3 overflow-y-auto">
          {details.map(detail => (
            <div key={detail.id} className={`p-4 border rounded-lg transition-all 
                ${detail.isCompleted ? 'bg-green-50 border-green-200' : 'bg-white'}`}>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">

                <div className="md:col-span-6 flex items-center gap-3">
                  <input
                    type="checkbox"
                    className="h-5 w-5 cursor-pointer accent-green-600"
                    checked={detail.isCompleted}
                    disabled={!canProcess || isAlreadyCompleted || isLoading}
                    onChange={() => handleToggle(detail.id)}
                  />
                  <div>
                    <span className={`text-sm font-semibold block ${detail.isCompleted ? 'text-green-800' : 'text-gray-700'}`}>
                      {detail.requirementDescription}
                    </span>
                    {detail.isCompleted && (
                      <span className="text-[10px] text-green-600 font-bold italic">
                        {detail.completedDate ? `Completed: ${new Date(detail.completedDate).toLocaleDateString()}` : 'COMPLETED'}
                      </span>
                    )}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <Input
                    placeholder="Enter BP"
                    value={detail.bp}
                    onChange={(e) => handleInputChange(detail.id, 'bp', e.target.value)}
                    disabled={!canProcess || isAlreadyCompleted || isLoading}
                  />
                </div>

                <div className="md:col-span-4">
                  <Input
                    placeholder="Enter Remarks"
                    value={detail.remarks}
                    onChange={(e) => handleInputChange(detail.id, 'remarks', e.target.value)}
                    disabled={!canProcess || isAlreadyCompleted || isLoading}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 border-t rounded-b-lg bg-gray-50 flex flex-wrap justify-end gap-2">
          <button onClick={onClose} className="px-6 py-2 bg-gray-400 text-white rounded border-none cursor-pointer font-bold transition-colors hover:bg-gray-500">
            Cancel
          </button>

          {canProcess && !isAlreadyCompleted && (
            <button
              onClick={handleSaveProgress}
              disabled={isLoading}
              className="px-6 py-2 bg-blue-600 text-white rounded border-none cursor-pointer font-bold flex items-center gap-2 hover:bg-blue-700 transition-colors shadow-md disabled:opacity-50"
            >
              <SaveIcon fontSize="small" />
              Save Progress
            </button>
          )}

          {canProcess && allItemsChecked && !isAlreadyCompleted && (
            <button
              onClick={() => setShowConfirm(true)}
              disabled={isLoading}
              className="px-6 py-2 bg-green-600 text-white rounded border-none cursor-pointer font-bold flex items-center gap-2 hover:bg-green-700 transition-colors shadow-md disabled:opacity-50"
            >
              <CheckCircleIcon fontSize="small" />
              Finalize Clearance
            </button>
          )}
        </div>

        <ConfirmationModal
          visible={showConfirm}
          onClose={() => setShowConfirm(false)}
          onConfirm={handleFinalize} // MODIFIED: handleFinalize now handles the double-action
          title="Mark Clearance as Complete?"
          message="This will save your current progress and finalize the clearance. Do you want to mark the employee as Fit-To-Work?"
          type="success"
          isLoading={isLoading}
        />
        <LoadingSpinner loading={localLoading || isLoading} />
      </div>
    </div>
  );
};

export default MedicalClearanceProcessorModal;